import { CircularProgress, Grid, Stack, Typography } from "@financeable-com-au/financeable-ui";
import { fCentsToDollars } from "@/utils/formatNumber";
import { ServiceUsagesSectionProps } from "@/utils/interface";
import { INVOICE_SECTIONS } from "@/constants";
import SectionCard from "./SectionCard";

type ColumnAlign = "left" | "right";

// 12-column grid spans (description 4 + qty/unit/tax/amount 2 each = 12)
const COLUMNS: { key: string; label: string; xs: number; align: ColumnAlign }[] = [
  { key: "description", label: "Description", xs: 4, align: "left" },
  { key: "qty", label: "Qty", xs: 2, align: "right" },
  { key: "unitPrice", label: "Unit price", xs: 2, align: "right" },
  { key: "tax", label: "Tax", xs: 2, align: "right" },
  { key: "amount", label: "Amount", xs: 2, align: "right" },
];

const Row = ({ children, sx }: { children: React.ReactNode; sx?: object }) => (
  <Grid container alignItems="center" sx={{ padding: "10px 12px", ...sx }}>
    {children}
  </Grid>
);

const Cell = ({
  children,
  xs,
  level = "body-sm",
  align = "left",
  bold = false,
  textColor,
}: {
  children: React.ReactNode;
  xs: number;
  level?: "body-xs" | "body-sm" | "title-sm";
  align?: ColumnAlign;
  bold?: boolean;
  textColor?: string;
}) => (
  <Grid xs={xs}>
    <Typography
      level={level}
      textColor={textColor}
      style={{
        textAlign: align,
        ...(bold ? { fontWeight: level === "title-sm" ? 700 : 600 } : {}),
      }}
    >
      {children}
    </Typography>
  </Grid>
);

const SummaryRow = ({
  label,
  value,
  emphasize = false,
}: {
  label: React.ReactNode;
  value: React.ReactNode;
  emphasize?: boolean;
}) => {
  const level = emphasize ? "title-sm" : "body-sm";
  return (
    <Row>
      <Cell xs={10} level={level} align="right" bold={emphasize} textColor={emphasize ? undefined : "neutral.700"}>
        {label}
      </Cell>
      <Cell xs={2} level={level} align="right" bold={emphasize}>
        {value}
      </Cell>
    </Row>
  );
};

const ServiceUsagesSection = ({ disabled, loading, usages, expanded, onExpandedChange }: ServiceUsagesSectionProps) => {
  const services = usages?.services ?? [];
  const summary = usages?.summary;
  const gstLabel = usages?.gstLabel || "GST";

  return (
    <SectionCard
      title={INVOICE_SECTIONS.serviceUsages}
      subtitle="Read-only snapshot of services used in the selected billing period."
      disabled={disabled}
      collapsible
      expanded={expanded}
      onExpandedChange={onExpandedChange}
    >
      <Stack direction="column" rowGap={0}>
        <Row sx={{ borderBottom: "1px solid", borderColor: "neutral.200" }}>
          {COLUMNS.map((c) => (
            <Cell key={c.key} xs={c.xs} level="body-xs" textColor="neutral.500" align={c.align} bold>
              {c.label}
            </Cell>
          ))}
        </Row>

        {loading && (
          <Stack alignItems="center" style={{ padding: "12px 0" }}>
            <CircularProgress size="sm" />
          </Stack>
        )}

        {!loading && services.length === 0 && (
          <Typography level="body-sm" textColor="neutral.500" style={{ textAlign: "center", padding: "8px 0" }}>
            No service usages for the selected period.
          </Typography>
        )}

        {!loading &&
          services.map((s) => (
            <Row key={s._id} sx={{ borderBottom: "1px solid", borderColor: "neutral.100" }}>
              <Cell xs={4} bold>
                {s.name}
              </Cell>
              <Cell xs={2} align="right">
                {s.usageCount}
              </Cell>
              <Cell xs={2} align="right">
                {fCentsToDollars(s.unitPriceDollars)}
              </Cell>
              <Cell xs={2} align="right">
                {s.taxPercentage ? `${s.taxPercentage}%` : ""}
              </Cell>
              <Cell xs={2} align="right">
                {fCentsToDollars(s.amount)}
              </Cell>
            </Row>
          ))}

        {!loading && summary && (
          <Stack direction="column" style={{ paddingTop: 12 }}>
            <SummaryRow label="Subtotal" value={fCentsToDollars(summary.subtotal)} />
            <SummaryRow label="Total excluding tax" value={fCentsToDollars(summary.totalExcludingTax)} />
            <SummaryRow
              label={`${gstLabel} (${summary.gstPercentage}% on ${fCentsToDollars(summary.gstAppliedOn)})`}
              value={fCentsToDollars(summary.gstAmount)}
            />
            <SummaryRow label="Credit card charges" value={fCentsToDollars(summary.creditCardAmount ?? 0)} />
            <SummaryRow label="Total" value={fCentsToDollars(summary.total)} emphasize />
          </Stack>
        )}
      </Stack>
    </SectionCard>
  );
};

export default ServiceUsagesSection;
